import { useDispatch, useSelector } from "react-redux";
import { selectTranslations } from "../../services/i18n/i18nSlice";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { setLoading, setNavbarHide, setNavbarHideMobile } from "../../services/global/globalSlice";
import { Link, useParams } from "react-router-dom";
import { Form } from "react-bootstrap";

import { proctoringSession } from "../../utils/proctoring";
import { enterFullscreen } from "../../utils/deviceManagement";

import arrow_img from '../../assets/images/arrow-back.svg'
import structure_img from './assets/images/structure.svg'

import { TitleH1 } from "../../components/TitleH1";
import { TitleH3 } from "../../components/TitleH3";
import { Text } from "../../components/Text";
import { Button } from "../../components/Button";
import { fetchGetExam, fetchStartCustomExam } from "./ExamsApi";
import { showError } from "../../features/Error/errorSlice";
import { setCurrentExam, selectCurrentProctoringSessionActive } from "../../features/Exam/examSlice";

const buildTopicsMap = (subjects) => {
    const map = {}
    ;(subjects || []).forEach(subject => {
        ;(subject.topics || []).forEach(topic => {
            map[topic.topic_id] = {
                topic_name: topic.topic_name,
                subject_name: subject.subject_name
            }
        })
    })
    return map
}

const buildModuleTopicRows = (modules) => {
    return (modules || []).map(module => {
        if (!module.topics || module.topics.length === 0) return null
        return module.topics.map(topic => ({
            topic_ids: topic.topic_ids,
            topic_names: topic.topic_names,
            max: topic.num_questions || 1,
            value: topic.num_questions || 1,
            enabled: true
        }))
    })
}

const buildModuleTimes = (modules) => {
    return (modules || []).map(module => module.time)
}

// mirrors the examStarted/examCompleted computation in ExamPreviewScreen so the two
// screens agree on when a fresh, customizable attempt is actually available
const hasActiveOrCompletedAttempt = (data) => {
    const isMultipleAttempts = data?.exam?.is_multiple_attempts
    const examStarted = (data?.start_date && data?.end_date && isMultipleAttempts)
        ? false
        : !!(data?.start_date && !data?.end_date)
    const examCompleted = !!(data?.start_date && data?.end_date && !isMultipleAttempts)
    return examStarted || examCompleted
}

export default function ExamCustomizeScreen(){

    const [examData, setExamData] = useState(null)
    const [topicsMap, setTopicsMap] = useState({})
    const [moduleTopicRows, setModuleTopicRows] = useState([])
    const [moduleTimes, setModuleTimes] = useState([])

    const t = useSelector(selectTranslations)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { exam_id } = useParams()

    const proctoringSessionActive = useSelector(selectCurrentProctoringSessionActive)

    const loadExamData = async () => {
        dispatch(setLoading(true))
        // deliberately never pass a user_exam_id here: this screen only makes sense for
        // a fresh attempt (guarded below), and reusing a stale/ended one would silently
        // resume its already-elapsed progress instead of starting anew
        const response = await fetchGetExam({ exam_id: exam_id })
        if (response.success) {
            if (hasActiveOrCompletedAttempt(response.data)) {
                // an attempt is already in progress, or fully completed with no retry available:
                // structure is locked, customization no longer applies.
                // ExamPreviewScreen requires location.state.exam.is_started to trigger its own full reload,
                // otherwise it bounces to /exams for lacking state entirely.
                navigate(`/exams/preview/${exam_id}`, { replace: true, state: { exam: { exam_id, is_started: true } } })
                return
            }
            setExamData(response.data?.exam)
            setTopicsMap(buildTopicsMap(response.data?.subjects))
            setModuleTopicRows(buildModuleTopicRows(response.data?.exam?.modules))
            setModuleTimes(buildModuleTimes(response.data?.exam?.modules))
            dispatch(setLoading(false))
        } else {
            console.error('fetchGetExam failed in ExamCustomizeScreen', response.error)
            dispatch(showError({
                title: t.exam.errors.start,
                text: t.exam.errors.start_text,
                redirect: "/exams",
                cta_text: t.exam.errors.go_back
            }))
        }
    }

    const renderTopicGroupLabel = (row) => {
        if (row.topic_names && row.topic_names.length > 0) {
            return row.topic_names.map((name, idx) => name || topicsMap[row.topic_ids[idx]]?.topic_name || row.topic_ids[idx]).join(', ')
        }
        return row.topic_ids.map(id => topicsMap[id]?.topic_name || id).join(', ')
    }

    const handleTopicValueChange = (moduleIndex, rowIndex, rawValue) => {
        let value = parseInt(rawValue, 10)
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            return rows.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row
                const boundedValue = Number.isNaN(value) ? 1 : Math.min(Math.max(value, 1), row.max)
                return { ...row, value: boundedValue }
            })
        }))
    }

    const handleToggleTopicRow = (moduleIndex, rowIndex) => {
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            const activeCount = rows.filter(row => row.enabled).length
            return rows.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row
                // at least one topic group must stay active per module
                if (row.enabled && activeCount <= 1) return row
                return { ...row, enabled: !row.enabled }
            })
        }))
    }

    const handleToggleAllTopics = (moduleIndex) => {
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            const allEnabled = rows.every(row => row.enabled)
            if (allEnabled) {
                // a module must keep at least one active topic group, so "deselect all" keeps the first one
                return rows.map((row, idx) => ({ ...row, enabled: idx === 0 }))
            }
            return rows.map(row => ({ ...row, enabled: true }))
        }))
    }

    const handleTimeChange = (moduleIndex, rawValue) => {
        let value = parseInt(rawValue, 10)
        setModuleTimes(prev => prev.map((time, idx) => {
            if (idx !== moduleIndex) return time
            return Number.isNaN(value) ? 1 : Math.max(value, 1)
        }))
    }

    const handleStartCustomExam = async () => {
        dispatch(setLoading(true))
        if (examData?.proctoring_required && proctoringSessionActive) {
            if (proctoringSession?.isWindowOpen()) {
                proctoringSession.start()
            } else {
                dispatch(showError({
                    title: t.proctoring.proctoring_not_found,
                    text: t.proctoring.proctoring_not_found_text,
                    cta_text: t.exam.errors.go_back
                }))
                dispatch(setLoading(false))
                return
            }
        }
        const payload = {
            modules: (examData?.modules || []).map((_, moduleIndex) => {
                const rows = moduleTopicRows[moduleIndex]
                const modulePayload = {}
                if (rows) {
                    modulePayload.topics = rows
                        .filter(row => row.enabled)
                        .map(row => ({ topic_ids: row.topic_ids, num_questions: row.value }))
                }
                // per-module time only makes sense when the exam isn't governed by a single global timer
                if (!examData?.time && moduleTimes[moduleIndex] !== undefined && moduleTimes[moduleIndex] !== null) {
                    modulePayload.time = moduleTimes[moduleIndex]
                }
                return modulePayload
            })
        }
        const response = await fetchStartCustomExam({ exam_id: exam_id }, payload)
        if (response.success) {
            dispatch(setCurrentExam({ exam_id: exam_id }))
            if (examData?.proctoring_required && proctoringSessionActive) {
                enterFullscreen()
            }
            navigate(examData?.time ? '/exams/perform/' : '/exams/start_module/', { replace: true, state: response.data })
        } else {
            console.error('fetchStartCustomExam failed in ExamCustomizeScreen', response.error)
            dispatch(showError({
                title: t.exam.errors.start,
                text: t.exam.errors.start_text,
                redirect: "/exams",
                cta_text: t.exam.errors.go_back
            }))
        }
    }

    useEffect(() => {
        dispatch(setNavbarHide(true))
        dispatch(setNavbarHideMobile(true))
        loadExamData()
        // eslint-disable-next-line
    }, [exam_id])

    return (
        <section id="examCustomizeScreen">
            <div className='section_header d-flex justify-content-between align-items-center'>
                <img src={arrow_img} className='pointer' alt="" onClick={() => navigate(-1)} />
                <Link to={'/account/contact'}>{t.account.need_help}</Link>
            </div>
            {examData ?
                <div style={{ maxWidth: "600px", minWidth: "320px" }} className="mx-auto">
                    <TitleH1 className="text-center">{t.exam.customize.title}</TitleH1>
                    <Text className="text-center">{t.exam.customize.subtitle}</Text>

                    {examData?.modules?.map((module, moduleIndex) => {
                        const rows = moduleTopicRows[moduleIndex]
                        const activeCount = rows ? rows.filter(row => row.enabled).length : 0
                        return (
                            <div className="border mt-3 mb-2 p-3" style={{ borderRadius: "16px" }} key={moduleIndex}>
                                <TitleH3 className="text-center"><img src={structure_img} alt="" className="me-2" />{module.module_name}</TitleH3>
                                {rows && rows.length > 1 ?
                                    <Form.Check
                                        type="checkbox"
                                        className="mt-3"
                                        label={t.exam.customize.select_all_topics}
                                        checked={rows.every(row => row.enabled)}
                                        onChange={() => handleToggleAllTopics(moduleIndex)}
                                    />
                                    : null}
                                {rows ?
                                    rows.map((row, rowIndex) => (
                                        <div className="d-flex justify-content-between align-items-center mt-3" key={rowIndex}>
                                            <Form.Check
                                                type="checkbox"
                                                className="me-2"
                                                checked={row.enabled}
                                                disabled={row.enabled && activeCount <= 1}
                                                onChange={() => handleToggleTopicRow(moduleIndex, rowIndex)}
                                            />
                                            <div className="flex-grow-1">
                                                <Text className={`mb-0 ${!row.enabled ? 'text-muted' : ''}`}>{renderTopicGroupLabel(row)}</Text>
                                                <small className="text-muted">{t.exam.customize.max_questions.replace('{max}', row.max)}</small>
                                            </div>
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                max={row.max}
                                                value={row.value}
                                                disabled={!row.enabled}
                                                style={{ width: "80px" }}
                                                onChange={(e) => handleTopicValueChange(moduleIndex, rowIndex, e.target.value)}
                                            />
                                        </div>
                                    ))
                                    :
                                    <Text className="mb-0 mt-2 text-muted">{t.exam.customize.fixed_module_notice}</Text>
                                }
                                {
                                    !examData?.time && moduleTimes[moduleIndex] !== undefined && moduleTimes[moduleIndex] !== null &&
                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                                        <Text className="mb-0">{t.exam.customize.module_time}</Text>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            value={moduleTimes[moduleIndex]}
                                            style={{ width: "80px" }}
                                            onChange={(e) => handleTimeChange(moduleIndex, e.target.value)}
                                        />
                                    </div>
                                }
                            </div>
                        )
                    })}

                    <div className="mt-4 mb-4">
                        <Button className="mx-auto" onClick={handleStartCustomExam}>{t.exam.customize.confirm}</Button>
                        <Button color="secondary" className="mx-auto mt-2" onClick={() => navigate(-1)}>{t.exam.customize.cancel}</Button>
                    </div>
                </div>
                : null}
        </section>
    )
}
